import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { GestureHandling } from "leaflet-gesture-handling";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";

// Enable the plugin globally: single-finger touch just scrolls the page,
// two fingers pan/zoom the map. Mouse wheel + Ctrl also required to zoom.
L.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);
import {
  ChevronLeft, Loader2, MapPin, Navigation, Car, Bike, Footprints,
  Landmark, GlassWater, Waves, Trees, Mountain, ShoppingBag, ExternalLink, Compass, Zap, Sparkles, Calendar,
} from "lucide-react";
import { fetchPois, fetchRestaurant } from "../lib/api";
import axios from "axios";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { SubPageNav } from "../components/SubPageNav";
import { Toaster } from "../components/ui/toaster";
import { tr, T } from "../lib/i18n";
import AppBottomBar from "../components/AppBottomBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  events:     { color: "#c48b31", accent: "bg-amber-100 text-amber-900 border-amber-200",       Icon: Sparkles, shape: "star" },
  culture:    { color: "#a83232", accent: "bg-red-100 text-red-900 border-red-200",             Icon: Landmark },
  nature:     { color: "#2e7d32", accent: "bg-emerald-100 text-emerald-900 border-emerald-200", Icon: Trees },
  shopping:   { color: "#8e24aa", accent: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200", Icon: ShoppingBag },
  adrenaline: { color: "#ef6c00", accent: "bg-orange-100 text-orange-900 border-orange-200",    Icon: Zap },
  winery:     { color: "#7b3f00", accent: "bg-amber-100 text-amber-900 border-amber-200",       Icon: GlassWater },
  // Backwards-compat mappings for POIs still using the old schema:
  monument:   { color: "#a83232", accent: "bg-red-100 text-red-900 border-red-200",             Icon: Landmark },
  beach:      { color: "#1e88e5", accent: "bg-sky-100 text-sky-900 border-sky-200",             Icon: Waves },
  park:       { color: "#2e7d32", accent: "bg-emerald-100 text-emerald-900 border-emerald-200", Icon: Trees },
  sport:      { color: "#ef6c00", accent: "bg-orange-100 text-orange-900 border-orange-200",    Icon: Mountain },
};

const S = {
  back:     { it: "Torna alla home",     en: "Back to home",       es: "Volver al inicio",     fr: "Retour à l'accueil",  de: "Zur Startseite" },
  title:    { it: "Punti di Interesse",   en: "Points of Interest",  es: "Puntos de Interés",     fr: "Points d'Intérêt",    de: "Sehenswürdigkeiten" },
  subtitle: { it: "Scopri il territorio del Fermano e delle Marche", en: "Discover the Fermo & Marche region", es: "Descubre la zona de Fermo y las Marcas", fr: "Découvrez la région de Fermo et des Marches", de: "Entdecke die Region Fermo & Marken" },
  all:      { it: "Tutto",                en: "All",                 es: "Todo",                  fr: "Tout",                de: "Alles" },
  useMyLoc: { it: "Usa la mia posizione", en: "Use my location",     es: "Usar mi ubicación",     fr: "Utiliser ma position",de: "Meinen Standort verwenden" },
  fromStay: { it: "Dalla struttura",       en: "From the property",   es: "Desde el alojamiento",  fr: "Depuis la propriété", de: "Vom Anwesen" },
  noPois:   { it: "Nessun luogo per questa categoria.", en: "No places in this category.", es: "Sin lugares en esta categoría.", fr: "Aucun lieu dans cette catégorie.", de: "Keine Orte in dieser Kategorie." },
  navigate: { it: "Portami qui",          en: "Take me there",       es: "Llévame allí",          fr: "M'y emmener",         de: "Route berechnen" },
  website:  { it: "Sito web",             en: "Website",             es: "Sitio web",             fr: "Site web",            de: "Website" },
  car:      { it: "auto",                 en: "car",                 es: "coche",                 fr: "voiture",             de: "Auto" },
  bike:     { it: "bici",                 en: "bike",                es: "bici",                  fr: "vélo",                de: "Rad" },
  foot:     { it: "a piedi",              en: "on foot",             es: "a pie",                 fr: "à pied",              de: "zu Fuß" },
  loading:  { it: "Caricamento…",         en: "Loading…",            es: "Cargando…",             fr: "Chargement…",         de: "Wird geladen…" },
  gettingLoc: { it: "Rilevo posizione…",  en: "Getting location…",   es: "Obteniendo ubicación…", fr: "Localisation…",       de: "Standort wird ermittelt…" },
};

// Map event categories to the POI colour palette so stars visually echo the
// closest thematic category (gastronomic → winery brown, religious/historic
// → culture red, sport → adrenaline orange, …).
const EVENT_CAT_TO_POI = {
  gastronomica: "winery",      // #7b3f00 — vino / degustazioni
  musicale:     "shopping",    // #8e24aa — fuchsia, distinto e allegro
  storica:      "culture",     // #a83232 — patrimonio storico
  religiosa:    "culture",     // #a83232 — culturale/religioso
  cultura:      "culture",     // #a83232
  sport:        "adrenaline",  // #ef6c00 — outdoor / adrenalina
};
function eventColor(ev) {
  const cat = ev?.category || ev?._event?.category;
  const targetKey = EVENT_CAT_TO_POI[cat];
  return (targetKey && CATEGORY_META[targetKey]?.color) || CATEGORY_META.events.color;
}
function eventAccent(ev) {
  const cat = ev?.category || ev?._event?.category;
  const targetKey = EVENT_CAT_TO_POI[cat];
  return (targetKey && CATEGORY_META[targetKey]?.accent) || CATEGORY_META.events.accent;
}

function buildIcon(color) {
  const html = `
    <span style="
      display:flex;align-items:center;justify-content:center;
      background:${color};color:#fff;
      width:20px;height:20px;border-radius:50%;
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);
    "></span>`;
  return L.divIcon({ html, className: "poi-marker", iconSize: [20, 20], iconAnchor: [10, 10], popupAnchor: [0, -10] });
}
function buildStarIcon(color) {
  // Filled 5-point star, slightly larger than circle markers so events pop.
  const html = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="26" height="26"
      style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.5));">
      <path d="M12 2 L14.9 8.6 L22 9.3 L16.7 14 L18.3 21 L12 17.3 L5.7 21 L7.3 14 L2 9.3 L9.1 8.6 Z"
        fill="${color}" stroke="#fff" stroke-width="1.4" stroke-linejoin="round" />
    </svg>`;
  return L.divIcon({ html, className: "poi-marker-star", iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -13] });
}
function propertyIcon(logoUrl) {
  // White pill with brown ring — mirrors the POI markers style but inverted,
  // so the starting point reads as consistent yet clearly distinct.
  const logo = logoUrl
    ? `<img src="${logoUrl}" alt="" style="width:100%;height:100%;object-fit:contain;" />`
    : `<span style="display:flex;align-items:center;justify-content:center;color:#4a1c17;font-size:16px;">🏛</span>`;
  const html = `<span style="
    display:flex;align-items:center;justify-content:center;
    background:#ffffff;
    width:38px;height:38px;border-radius:50%;
    border:3px solid #4a1c17;
    box-shadow:0 2px 6px rgba(0,0,0,.35);
    padding:4px;box-sizing:border-box;
    ">${logo}</span>`;
  return L.divIcon({ html, className: "poi-marker-home", iconSize: [38, 38], iconAnchor: [19, 19], popupAnchor: [0, -18] });
}

function FitBoundsOnData({ pois, propertyPos, refitKey, includeProperty, enabled }) {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    if (!pois || pois.length === 0) return;
    if (lastKey.current === refitKey) return;
    lastKey.current = refitKey;
    const points = pois.map((p) => [p.lat, p.lon]);
    if (includeProperty) points.unshift([propertyPos.lat, propertyPos.lon]);
    if (points.length === 1) {
      map.setView(points[0], 13, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.invalidateSize();
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 14, animate: true });
  }, [pois, propertyPos, map, refitKey, includeProperty, enabled]);
  return null;
}

export default function POIsPage() {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventCatLabels, setEventCatLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [origin, setOrigin] = useState(null); // {lat, lon} — null = property
  const [geoBusy, setGeoBusy] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [selectedId, setSelectedId] = useState(null);
  const [nearOnly, setNearOnly] = useState(false); // filter ≤20km
  const markerRefs = useRef({});           // id → Leaflet marker instance
  const mapRef = useRef(null);             // Leaflet map instance
  const mapContainerRef = useRef(null);    // DOM node for scroll-into-view

  const categoriesRef = useRef(null);      // DOM node for filter chip row
  const categoriesAnchorRef = useRef(null); // Non-sticky anchor placed just before the chip row

  const [pendingScroll, setPendingScroll] = useState(false);

  // Shared helper: scrolls the page so the chip row lands EXACTLY at its
  // sticky lock position (right under the 56px brand bar).
  //
  // We use an explicit `window.scrollTo(targetY)` computed from the
  // anchor's `getBoundingClientRect()` rather than `scrollIntoView`,
  // because `scrollIntoView` misbehaves when the target is adjacent to
  // a `position: sticky` sibling that is currently pinned — the browser
  // often clamps the smooth scroll far short of the natural anchor
  // position. Reading the rect at call-time and passing an absolute Y
  // value guarantees the page lands at the correct spot every time,
  // whether we are switching categories from the top or from deep down
  // in the list.
  const scrollToStickyLock = () => {
    const anchor = categoriesAnchorRef.current || mapContainerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const targetY = Math.max(0, rect.top + window.scrollY - 56);
    window.scrollTo({ top: targetY, behavior: "auto" });
  };

  // Chip click: switch category AND replay the same layout as clicking
  // a marker — chips pinned, map visible, first POI of the new category
  // selected so its marker highlights and its card is next.
  //
  // We do NOT scroll immediately here — the new category triggers a
  // fetch, the list unmounts (loading=true) and the browser's built-in
  // "scroll anchoring" would jerk the scroll back once the new list
  // grows the page again. Instead we set `pendingScroll` and let the
  // dedicated useEffect run scrollToStickyLock AFTER loading flips
  // back to false and the new list is committed.
  const selectCategory = (cat) => {
    setCategory(cat);
    setSelectedId(null);
    setPendingScroll(true);
  };

  // Run the scroll AFTER the new list has rendered (loading has flipped
  // back to false following a category switch). This defeats the
  // scroll-anchoring reflow that otherwise strands the map off-screen.
  useEffect(() => {
    if (!pendingScroll) return;
    if (loading) return;
    // Two rAFs: first waits for React commit, second waits for the
    // browser to paint + adjust any sticky/anchor offsets.
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        scrollToStickyLock();
        setPendingScroll(false);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScroll, loading]);

  const openMarkerPopup = (poi) => {
    setSelectedId(poi.id);
    scrollToStickyLock();
    const marker = markerRefs.current[poi.id];
    const map = mapRef.current;
    if (marker && map) {
      setTimeout(() => {
        // Show BOTH the property (starting point) and the selected POI in the
        // viewport so the guest always sees the relationship between the two.
        const bounds = L.latLngBounds([
          [property.lat, property.lon],
          [poi.lat, poi.lon],
        ]);
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13, animate: true });
        marker.openPopup();
      }, 350);
    }
  };

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);

  useEffect(() => { fetchRestaurant().then(setRestaurant).catch(() => {}); }, []);

  // Events are always fetched (client-side filter). We reload only when the
  // origin changes so distances stay in sync with POIs.
  useEffect(() => {
    axios.get(`${API}/events`)
      .then((r) => {
        setEvents(r.data.events || []);
        setEventCatLabels(r.data.category_labels || {});
      })
      .catch(() => setEvents([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    // Ask backend for ALL categories — we filter client-side so events can be
    // merged into the same list without duplicating the request.
    fetchPois(category === "events" ? "all" : category, origin)
      .then((d) => setData(d))
      .catch(() => setData({ pois: [], categories: [], category_labels: {}, property: { lat: 43.184, lon: 13.531 } }))
      .finally(() => setLoading(false));
  }, [category, origin]);

  const property = data?.property || { lat: 43.184, lon: 13.531 };
  const originPos = origin || property;

  // Haversine (mirrors backend logic) so events show distance too.
  function haversineKm(a, b) {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const s1 = Math.sin(dLat / 2);
    const s2 = Math.sin(dLon / 2);
    const c = s1 * s1 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * s2 * s2;
    return Math.round(2 * R * Math.asin(Math.sqrt(c)) * 10) / 10;
  }
  function eventToPoi(e) {
    const km = haversineKm(originPos, { lat: e.lat, lon: e.lon });
    return {
      id: `event-${e.id}`,
      _isEvent: true,
      _event: e,
      category: "events",
      name: e.name,
      description: e.description,
      image: e.image,
      lat: e.lat, lon: e.lon,
      website: e.website,
      distance_km: km,
      time_car_min: Math.max(1, Math.round((km / 60) * 60)),
      time_bike_min: Math.max(2, Math.round((km / 15) * 60)),
      time_foot_min: Math.max(5, Math.round((km / 5) * 60)),
    };
  }

  // Merge POIs + Events. If a category is selected, filter to it.
  const rawPois = data?.pois || [];
  const eventsAsPois = events.map(eventToPoi);
  let merged;
  if (category === "events") merged = eventsAsPois;
  else if (category === "all") merged = [...eventsAsPois, ...rawPois];
  else merged = rawPois; // only POI cats — events excluded
  const pois = nearOnly ? merged.filter((p) => p.distance_km <= 20) : merged;

  // Categories: put "events" first, then POI categories
  const categories = ["events", ...(data?.categories || [])];
  const labels = {
    ...(data?.category_labels || {}),
    events: { it: "Sagre & Eventi", en: "Events", es: "Eventos", fr: "Événements", de: "Feste" },
  };

  // Currently-selected POI (if any) — used to render the pinned card under the map.
  const selectedPoi = pois.find((p) => p.id === selectedId) || null;

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setOrigin({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGeoBusy(false); },
      () => { setGeoBusy(false); alert("Impossibile ottenere la posizione."); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };
  const resetOrigin = () => setOrigin(null);

  const openInMaps = (p) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lon}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#faf3ee]">
      <RestaurantHeader
        restaurant={restaurant}
        lang={lang}
        setLang={setLang}
        compact
        stickyBar
      />
      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="pois-page">
        <button onClick={() => navigate("/")} className="hidden">
          <ChevronLeft className="h-4 w-4" /> {tr(S.back, lang)}
        </button>
        <SubPageNav lang={lang} />
        <div className="flex items-end gap-3 mb-4">
          <div className="min-w-0">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2b1410] leading-tight">{tr(S.title, lang)}</h1>
            <p className="text-sm text-[#7c5a4e]">{tr(S.subtitle, lang)}</p>
          </div>
        </div>

        {/* Invisible spacer sitting AT the natural document position of
            the sticky chip row. Its rect.top always reflects that real
            static position, so `openMarkerPopup` can compute the scroll
            target correctly even when the chips are already pinned. */}
        <div ref={categoriesAnchorRef} aria-hidden className="h-0" />
        {/* Category filters — sticky just below the brand bar so users
            keep the filter row in reach while they scroll the list. */}
        <div
          ref={categoriesRef}
          className="sticky z-[9998] -mx-4 px-4 py-2 bg-[#f7ebdf]/95 backdrop-blur-md border-b border-[#e8dcc8] flex gap-2 overflow-x-auto no-scrollbar mb-3"
          style={{ top: "56px" }}
          data-testid="poi-categories"
        >
          <FilterChip active={category === "all"} onClick={() => selectCategory("all")} label={tr(S.all, lang)} />
          {categories.map((c) => {
            const Icon = CATEGORY_META[c]?.Icon || MapPin;
            return (
              <FilterChip
                key={c}
                active={category === c}
                onClick={() => selectCategory(c)}
                label={tr(labels[c] || { it: c }, lang)}
                icon={<Icon className="h-3.5 w-3.5" />}
                accent={CATEGORY_META[c]?.color}
                testId={`poi-cat-${c}`}
              />
            );
          })}
        </div>

        {/* Map */}
        <div ref={mapContainerRef} className="relative z-0 isolate rounded-2xl overflow-hidden border border-[#e8dcc8] shadow-sm bg-white h-[42vh] sm:h-[50vh]" data-testid="poi-map">
          {/* Geolocation control floating over the map (top-right, above Leaflet panes) */}
          <div className="absolute top-3 right-3 z-[1000]">
            {origin ? (
              <button onClick={resetOrigin} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] px-3 py-1.5 text-xs font-semibold text-[#4a1c17] shadow-md hover:bg-[#faf3ee]" data-testid="poi-reset-origin">
                <MapPin className="h-3.5 w-3.5" /> {tr(S.fromStay, lang)}
              </button>
            ) : (
              <button onClick={useMyLocation} disabled={geoBusy} className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold shadow-md disabled:opacity-60" data-testid="poi-use-mylocation">
                {geoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Compass className="h-3.5 w-3.5" />}
                {geoBusy ? tr(S.gettingLoc, lang) : tr(S.useMyLoc, lang)}
              </button>
            )}
          </div>
          <MapContainer
            center={[property.lat, property.lon]}
            zoom={10}
            scrollWheelZoom={false}
            gestureHandling={true}
            gestureHandlingOptions={{
              text: {
                touch: "Usa due dita per muovere la mappa",
                scroll: "Usa Ctrl + rotella per fare zoom",
                scrollMac: "Usa \u2318 + rotella per fare zoom",
              },
              duration: 1200,
            }}
            className="h-full w-full"
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBoundsOnData
              pois={pois}
              propertyPos={property}
              refitKey={`${category}|${nearOnly ? "near" : "all"}|${pois.length}`}
              includeProperty={category === "all"}
              enabled={!loading}
            />
            <Marker position={[property.lat, property.lon]} icon={propertyIcon(restaurant?.logo)}>
              <Popup><strong>{restaurant?.name || "Il Pozzo dei Giganti"}</strong></Popup>
            </Marker>
            {pois.map((p) => {
              const meta = CATEGORY_META[p.category] || CATEGORY_META.monument;
              const starColor = p._isEvent ? eventColor(p._event) : meta.color;
              const icon = meta.shape === "star" ? buildStarIcon(starColor) : buildIcon(meta.color);
              return (
                <Marker
                  key={p.id}
                  position={[p.lat, p.lon]}
                  icon={icon}
                  ref={(ref) => { if (ref) markerRefs.current[p.id] = ref; }}
                  eventHandlers={{ click: () => openMarkerPopup(p) }}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold text-neutral-900">{tr(p.name, lang)}</div>
                      {p._isEvent && p._event && (
                        <div className="text-[11px] text-[#7c5a4e] mt-0.5 inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {p._event.date_start} → {p._event.date_end}
                        </div>
                      )}
                      <div className="text-[11px] text-[#7c5a4e] mt-0.5">{p.distance_km} km · {p.time_car_min} min {tr(S.car, lang)}</div>
                      <button
                        onClick={() => openInMaps(p)}
                        className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-2 py-1 text-[11px]"
                      >
                        <Navigation className="h-3 w-3" /> {tr(S.navigate, lang)}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* Selected POI card — pinned right below the map for simultaneous
            reading + map view. Falls back gracefully if nothing selected. */}
        {selectedPoi && (
          <div className="mt-3" data-testid="poi-selected-card">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-[#7c5a4e] uppercase tracking-wide">
                {lang === "en" ? "Selected" : lang === "de" ? "Ausgewählt" : lang === "fr" ? "Sélection" : lang === "es" ? "Seleccionado" : "Selezionato"}
              </span>
              <button
                onClick={() => setSelectedId(null)}
                className="ml-auto text-xs text-[#7c5a4e] hover:text-[#4a1c17] underline"
                data-testid="poi-clear-selection"
              >
                {lang === "en" ? "close" : lang === "de" ? "schließen" : lang === "fr" ? "fermer" : lang === "es" ? "cerrar" : "chiudi"}
              </button>
            </div>
            <POICard
              poi={selectedPoi}
              lang={lang}
              labels={labels}
              eventCatLabels={eventCatLabels}
              highlighted
              onNavigate={() => openInMaps(selectedPoi)}
              onOpenPopup={() => openMarkerPopup(selectedPoi)}
            />
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
        ) : pois.length === 0 ? (
          <div className="mt-4 rounded-lg bg-white border border-[#e8dcc8] p-6 text-center text-sm text-[#7c5a4e]">{tr(S.noPois, lang)}</div>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="poi-list">
            {pois.map((p) => (
              <POICard
                key={p.id}
                poi={p}
                lang={lang}
                labels={labels}
                eventCatLabels={eventCatLabels}
                highlighted={selectedId === p.id}
                onNavigate={() => openInMaps(p)}
                onOpenPopup={() => openMarkerPopup(p)}
              />
            ))}
          </div>
        )}
      </main>
      <Toaster />
      <AppBottomBar current="none" lang={lang} />
    </div>
  );
}

function FilterChip({ active, onClick, label, icon, accent, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
        active ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#2b1410] border-[#e8dcc8] hover:border-[#4a1c17]/40"
      }`}
      style={active && accent ? { backgroundColor: accent, borderColor: accent } : undefined}
    >
      {icon}{label}
    </button>
  );
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
// Mix a hex colour with white to produce a pastel tint (0 = original, 1 = white).
function lightenHex(hex, amount = 0.82) {
  const { r, g, b } = hexToRgb(hex);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function POICard({ poi, lang, labels, eventCatLabels, highlighted, onNavigate, onOpenPopup }) {
  const meta = CATEGORY_META[poi.category] || CATEGORY_META.monument;
  // For events, use the subcategory-mapped colour palette (winery brown for
  // gastronomic, culture red for historic/religious, adrenaline orange for
  // sport, …) so the card echoes the star on the map.
  const isEvent = poi._isEvent;
  const badgeHex = isEvent ? eventColor(poi._event) : meta.color;
  // Solid pastel background = same colour lightened ~82% (readable on top of
  // photos, no more washed-out translucent look).
  const pillBg = lightenHex(badgeHex, 0.82);
  const pillStyle = {
    backgroundColor: pillBg,
    color: badgeHex,
    borderColor: `${badgeHex}66`,
  };
  const catLabel = isEvent
    ? (eventCatLabels?.[poi._event?.category]?.[lang] || eventCatLabels?.[poi._event?.category]?.it || tr(labels[poi.category] || {}, lang))
    : tr(labels[poi.category] || {}, lang);
  const CatIcon = isEvent ? Sparkles : meta.Icon;
  return (
    <div
      id={`poi-card-${poi.id}`}
      data-testid={`poi-card-${poi.id}`}
      className={`rounded-xl bg-white border shadow-sm overflow-hidden transition-all ${highlighted ? "ring-2 ring-[#4a1c17]" : "border-[#e8dcc8]"}`}
    >
      {poi.image ? (
        <button
          type="button"
          onClick={onOpenPopup}
          className="relative h-28 w-full block bg-neutral-100 text-left"
          data-testid={`poi-open-popup-${poi.id}`}
        >
          <img src={poi.image} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border" style={pillStyle}>
            <CatIcon className="h-3 w-3" /> {catLabel}
          </div>
        </button>
      ) : (
        <button type="button" onClick={onOpenPopup} className="h-14 w-full flex items-center gap-2 px-3 text-left border-b" style={pillStyle} data-testid={`poi-open-popup-${poi.id}`}>
          <CatIcon className="h-4 w-4" />
          <span className="text-[11px] font-bold uppercase tracking-wide">{catLabel}</span>
        </button>
      )}
      <button
        type="button"
        onClick={onOpenPopup}
        className="p-4 w-full text-left block hover:bg-[#faf3ee]/60 transition-colors"
      >
        <h3 className="text-base sm:text-lg font-bold text-neutral-900 leading-snug tracking-tight">{tr(poi.name, lang)}</h3>
        {isEvent && poi._event && (
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2 py-0.5 border" style={pillStyle}>
            <Calendar className="h-3 w-3" />
            <span>{poi._event.date_start} → {poi._event.date_end}</span>
            {poi._event.town && <span className="opacity-70">· {poi._event.town}</span>}
          </div>
        )}
        {poi.description && tr(poi.description, lang) && (
          <p className="mt-1 text-sm text-neutral-700 leading-relaxed">{tr(poi.description, lang)}</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#7c5a4e]">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-[#4a1c17]" /> <strong className="text-[#2b1410]">{poi.distance_km} km</strong></span>
          <span className="inline-flex items-center gap-1"><Car className="h-3 w-3 text-[#4a1c17]" /> {poi.time_car_min} min</span>
          <span className="inline-flex items-center gap-1"><Bike className="h-3 w-3 text-[#4a1c17]" /> {poi.time_bike_min} min</span>
          <span className="inline-flex items-center gap-1"><Footprints className="h-3 w-3 text-[#4a1c17]" /> {poi.time_foot_min} min</span>
        </div>
      </button>
      <div className="px-4 pb-4 flex gap-2">
        <button
          onClick={onNavigate}
          data-testid={`poi-navigate-${poi.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-2 text-sm font-medium"
        >
          <Navigation className="h-4 w-4" /> {tr(S.navigate, lang)}
        </button>
        {poi.website && (
          <a
            href={poi.website} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee] px-3 py-2 text-sm"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
